import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useLocation, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { Checkbox } from '../ui/Checkbox/Checkbox';
import { storageKey, HOME_ROUTE } from '@/config/app';
import { appConfig } from '@/config/app';
import { AuthService } from '@/services/AuthService';
import type { AuthProvidersConfig } from '@/services/AuthService';
import { t } from '@/lib/i18n';

const authSvc = new AuthService();

/** SVG icons for OAuth providers */
const providerIcons: Record<string, React.ReactNode> = {
  google: (
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
      <path fill="#34A853" d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.565 24 12.255 24z"/>
      <path fill="#FBBC05" d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z"/>
      <path fill="#EA4335" d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.69 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"/>
    </svg>
  ),
  microsoft: (
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
      <rect fill="#F25022" x="1" y="1" width="10" height="10"/>
      <rect fill="#7FBA00" x="13" y="1" width="10" height="10"/>
      <rect fill="#00A4EF" x="1" y="13" width="10" height="10"/>
      <rect fill="#FFB900" x="13" y="13" width="10" height="10"/>
    </svg>
  ),
};

export function AuthPage() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const location = useLocation() as unknown as { state?: { from?: string } };
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('error');

  React.useEffect(() => {
    if (user) {
      const checkOnboarding = async () => {
        try {
          // Super admin goes straight to admin panel
          if ((user as any)?.isSuperAdmin) {
            navigate('/admin', { replace: true });
            return;
          }
          const redirectOnboarding = localStorage.getItem(storageKey('redirect_onboarding')) === '1';
          if (redirectOnboarding) {
            localStorage.removeItem(storageKey('redirect_onboarding'));
            navigate('/onboarding', { replace: true });
            return;
          }
          const { useOrgStore } = await import('@/stores/orgStore');
          const orgState = useOrgStore.getState();
          const orgs = orgState.orgs;
          const activeId = orgState.activeOrgId;
          const active = orgs.find((o: any) => o.id === activeId) || orgs[0];
          if (active) {
            let completed = Boolean(active.onboarding_completed);
            if (!completed) {
              try { completed = localStorage.getItem(storageKey(`onboarding_completed_${active.id}`)) === '1'; } catch {}
            }
            const destination = completed ? HOME_ROUTE : '/onboarding';
            navigate(destination, { replace: true });
            return;
          }
        } catch (e) {}
        const from = (location?.state?.from as string | undefined) || HOME_ROUTE;
        navigate(from, { replace: true });
      };
      checkOnboarding();
    }
  }, [user, navigate, location]);

  if (user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <img
              src="/images/logo.png"
              alt={appConfig.name}
              className="h-12 mx-auto mb-4 object-contain"
            />
            <p className="text-sm text-muted-foreground mt-1" data-no-translate>Organizational Excellence Operating System</p>
          </Link>
        </div>

        {/* Auth Card */}
        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground">Sign In</h2>
              <p className="text-sm text-muted-foreground mt-1">Access your account</p>
            </div>
            {oauthError && (
              <Alert variant="error" className="mb-4">
                {decodeURIComponent(oauthError)}
              </Alert>
            )}
            <LoginForm />
          </div>
        </div>

        {/* Register CTA */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Don't have an organization yet?{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-800 font-medium">
            Create one
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-xs text-muted-foreground">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const login = useAuthStore(s => s.login);
  const isLoading = useAuthStore(s => s.isLoading);
  const error = useAuthStore(s => s.error);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(true);

  // Auth mode: 'password' | 'magic_link'
  const [authMode, setAuthMode] = React.useState<'password' | 'magic_link'>('password');
  const [magicLinkSent, setMagicLinkSent] = React.useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = React.useState(false);
  const [magicLinkError, setMagicLinkError] = React.useState<string | null>(null);

  // Fetch available providers
  const [config, setConfig] = React.useState<AuthProvidersConfig | null>(null);
  React.useEffect(() => {
    authSvc.getProviders().then(setConfig).catch(() => {});
  }, []);

  const hasLocal = config?.providers.some(p => p.type === 'local') ?? true;
  const hasMagicLink = config?.magicLinkEnabled ?? false;
  const oauthProviders = config?.providers.filter(p => p.type !== 'local') ?? [];

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password, remember);
  };

  const onRequestMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setMagicLinkLoading(true);
    setMagicLinkError(null);
    try {
      await authSvc.requestMagicLink(email);
      setMagicLinkSent(true);
    } catch (err) {
      setMagicLinkError(err instanceof Error ? err.message : t('Failed to send magic link'));
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleOAuthStart = (providerType: string) => {
    // Redirect to OAuth start endpoint — the server handles the redirect
    const apiBase = (import.meta as any).env?.VITE_API_URL || '';
    window.location.href = `${apiBase}/api/auth/oauth/${providerType}/start`;
  };

  // Magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className="text-center py-6">
        <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Check your email</h3>
        <p className="text-sm text-muted-foreground mb-4">
          We sent a sign-in link to <strong>{email}</strong>. Click the link in the email to sign in.
        </p>
        <button
          onClick={() => { setMagicLinkSent(false); setAuthMode('password'); }}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* OAuth Providers */}
      {oauthProviders.length > 0 && (
        <div className="space-y-2">
          {oauthProviders.map((provider) => (
            <Button
              key={provider.type}
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleOAuthStart(provider.type)}
            >
              {providerIcons[provider.type] || null}
              Continue with {provider.displayName}
            </Button>
          ))}
        </div>
      )}

      {/* Divider (shown if there are OAuth providers AND local/magic link) */}
      {oauthProviders.length > 0 && (hasLocal || hasMagicLink) && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-background text-muted-foreground">Or continue with email</span>
          </div>
        </div>
      )}

      {/* Magic Link mode */}
      {authMode === 'magic_link' && hasMagicLink && (
        <form onSubmit={onRequestMagicLink} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          {magicLinkError && <Alert variant="error">{magicLinkError}</Alert>}
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={magicLinkLoading}
          >
            Send sign-in link
          </Button>
          {hasLocal && (
            <button
              type="button"
              onClick={() => setAuthMode('password')}
              className="w-full text-sm text-primary-600 hover:text-primary-700"
            >
              Sign in with password instead
            </button>
          )}
        </form>
      )}

      {/* Password mode (default) */}
      {authMode === 'password' && hasLocal && (
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              checked={remember}
              onChange={e => setRemember(e.target.checked)}
              label="Remember me"
            />
            <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">Forgot password?</Link>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={isLoading}
          >
            Sign in
          </Button>

          {hasMagicLink && (
            <button
              type="button"
              onClick={() => setAuthMode('magic_link')}
              className="w-full text-sm text-primary-600 hover:text-primary-700"
            >
              Sign in with a magic link instead
            </button>
          )}
        </form>
      )}

      {/* Fallback: no OAuth providers configured, show disabled Google placeholder */}
      {oauthProviders.length === 0 && !hasMagicLink && hasLocal && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
            </div>
          </div>
          <Button type="button" variant="outline" className="w-full" disabled>
            {providerIcons.google}
            Google (Coming soon)
          </Button>
        </>
      )}
    </div>
  );
}
